// Accessibility: what a screen reader can make of this document.
//
// Two deliberate choices about honesty run through this panel. It never
// says "accessible" — an automated pass rules things out, it cannot rule
// anything in, and the header says so. And a check with no honest
// automatic repair gets no Fix button: tagging and alternate text are
// content decisions, and a button that faked them would produce a file
// that passes checkers and reads as nonsense.
//
// Checks that another part of the editor already answers (OCR for a
// scanned page, Security for an extraction ban) link across to it
// rather than growing a second copy of that tool here.
import { useMemo, useState } from "react";
import {
  a11yScore,
  a11ySummary,
  auditAccessibility,
  type A11yFacts,
  type A11yFinding,
  type A11yFixId,
  type A11yGoTo,
  type A11yOverlayFacts,
  type A11yStatus,
} from "../lib/a11y.ts";
import { A11Y_LANGUAGES } from "../lib/a11y.ts";

const STATUS_META: Record<A11yStatus, { icon: string; className: string; label: string }> = {
  fail: { icon: "ri-close-circle-fill", className: "text-[#e03131]", label: "Needs fixing" },
  warn: { icon: "ri-error-warning-fill", className: "text-[#f08c00]", label: "Worth checking" },
  pass: { icon: "ri-checkbox-circle-fill", className: "text-[#2f9e44]", label: "Passes" },
  na: { icon: "ri-subtract-line", className: "text-[color:var(--color-fg-muted)]", label: "Not applicable" },
};

/** Where each cross-link goes, in words the button can wear. */
const GOTO_LABEL: Record<A11yGoTo, string> = {
  ocr: "Run OCR…",
  security: "Open Security…",
  properties: "Open Properties",
  comments: "Open Comments",
  fonts: "Open Fonts",
};

const ORDER: A11yStatus[] = ["fail", "warn", "pass", "na"];

export function AccessibilityPanel({
  facts,
  error,
  overlay,
  suggestedTitle,
  busy,
  paneWidth,
  onClose,
  onFix,
  onGoTo,
}: {
  facts: A11yFacts | null;
  /** Why the check couldn't run, if it couldn't. */
  error: string | null;
  overlay: A11yOverlayFacts;
  /** Offered as the document title when it has none — the file name,
   *  tidied. The user can always type a better one in Properties. */
  suggestedTitle: string;
  busy: boolean;
  paneWidth: number;
  onClose: () => void;
  onFix: (fix: A11yFixId, value?: string) => void;
  onGoTo: (to: A11yGoTo) => void;
}) {
  // The picker defaults to the browser's language, which is a better
  // guess than "English" and still only a guess.
  const [lang, setLang] = useState(() => {
    const nav = typeof navigator === "undefined" ? "" : navigator.language;
    const exact = A11Y_LANGUAGES.find((l) => l.code.toLowerCase() === nav.toLowerCase());
    if (exact) return exact.code;
    const base = nav.split("-")[0]?.toLowerCase();
    return A11Y_LANGUAGES.find((l) => l.code.toLowerCase().startsWith(base ?? ""))?.code ?? "en-AU";
  });
  const [showPasses, setShowPasses] = useState(false);

  const findings = useMemo(
    () => (facts ? auditAccessibility(facts, overlay) : []),
    [facts, overlay]
  );
  const score = useMemo(() => a11yScore(findings), [findings]);

  const grouped = ORDER.map((status) => ({
    status,
    items: findings.filter((f) => f.status === status),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]"
      style={{ width: paneWidth }}
    >
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-wheelchair-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Accessibility</h2>
        <button
          type="button"
          title="Close"
          aria-label="Close accessibility"
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {!facts && !error && (
          <p className="text-xs text-[color:var(--color-fg-muted)]">Checking the document…</p>
        )}
        {!facts && error && (
          <p className="rounded-md bg-[color:var(--color-bg-subtle)] p-2 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
            Couldn't check this document: {error}
          </p>
        )}

        {facts && (
          <>
            <div className="mb-3 rounded-md bg-[color:var(--color-bg-subtle)] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold">{a11ySummary(score)}</span>
                {busy && (
                  <i
                    className="ri-loader-4-line ml-auto animate-spin text-[color:var(--color-fg-muted)]"
                    aria-hidden
                  />
                )}
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
                These checks can only rule problems out. A document that passes all of them can
                still be unreadable — whether the alternate text is <em>right</em>, or the reading
                order makes sense, is something only a person can tell you.
              </p>
            </div>

            {grouped.map((group) => (
              <section key={group.status} className="mb-3">
                <h3 className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
                  <i className={`${STATUS_META[group.status].icon} ${STATUS_META[group.status].className}`} aria-hidden />
                  {STATUS_META[group.status].label} ({group.items.length})
                  {(group.status === "pass" || group.status === "na") && (
                    <button
                      type="button"
                      onClick={() => setShowPasses((v) => !v)}
                      className="ml-auto rounded px-1 text-[10px] font-normal normal-case hover:bg-[color:var(--color-bg-subtle)]"
                    >
                      {showPasses ? "Hide" : "Show"}
                    </button>
                  )}
                </h3>

                {(group.status === "fail" || group.status === "warn" || showPasses) && (
                  <ul className="space-y-1.5">
                    {group.items.map((f) => (
                      <FindingRow
                        key={f.id}
                        finding={f}
                        busy={busy}
                        lang={lang}
                        suggestedTitle={suggestedTitle}
                        onLangChange={setLang}
                        onFix={onFix}
                        onGoTo={onGoTo}
                      />
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function FindingRow({
  finding,
  busy,
  lang,
  suggestedTitle,
  onLangChange,
  onFix,
  onGoTo,
}: {
  finding: A11yFinding;
  busy: boolean;
  lang: string;
  suggestedTitle: string;
  onLangChange: (code: string) => void;
  onFix: (fix: A11yFixId, value?: string) => void;
  onGoTo: (to: A11yGoTo) => void;
}) {
  const meta = STATUS_META[finding.status];
  const canFix = finding.fixId && !(finding.fixId === "title" && !suggestedTitle);

  return (
    <li className="rounded-md border border-[color:var(--color-border)] p-2">
      <div className="flex items-start gap-1.5">
        <i className={`${meta.icon} ${meta.className} mt-px shrink-0`} aria-hidden />
        <span className="text-xs font-medium leading-snug">{finding.title}</span>
      </div>
      <p className="mt-1 pl-5 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
        {finding.detail}
      </p>
      {finding.fix && (
        <p className="mt-1 pl-5 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
          {finding.fix}
        </p>
      )}

      {(canFix || finding.goTo) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-5">
          {finding.fixId === "lang" && (
            <select
              value={lang}
              onChange={(e) => onLangChange(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label="Document language"
              className="h-7 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-[11px]"
            >
              {A11Y_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          )}
          {canFix && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                onFix(
                  finding.fixId!,
                  finding.fixId === "lang"
                    ? lang
                    : finding.fixId === "title"
                      ? suggestedTitle
                      : undefined
                )
              }
              className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-2 text-[11px] text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
            >
              <i className="ri-magic-line" aria-hidden />
              {finding.fixId === "title" ? `Use “${suggestedTitle}”` : "Fix"}
            </button>
          )}
          {finding.goTo && (
            <button
              type="button"
              onClick={() => onGoTo(finding.goTo!)}
              className="inline-flex h-7 items-center rounded-md border border-[color:var(--color-border)] px-2 text-[11px] hover:bg-[color:var(--color-bg-subtle)]"
            >
              {GOTO_LABEL[finding.goTo]}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
